import axios from 'axios';
import { useAuth0 } from '@auth0/auth0-react';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { Form, message, Modal, Select, Input, Button, Flex } from 'antd';
import { FC } from 'react';
import { useBackoffice } from '../backoffice/useBackoffice';

export const CreateCustomerModal: FC<{ open: boolean; onClose: () => void }> = ({ open, onClose }) => {
  const { developerId } = useBackoffice();
  const { getAccessTokenSilently } = useAuth0();
  const [messageApi, contextHolder] = message.useMessage();
  const queryClient = useQueryClient();
  const createCustomerMutation = useMutation({
    mutationKey: ['create-customer'],
    mutationFn: async (payload) => {
      const token = await getAccessTokenSilently();
      const res = await axios.post(`${process.env.REACT_APP_BACKEND_API_URL}/customers`, payload, {
        params: {
          consumerId: developerId,
        },
        headers: {
          Authorization: `Bearer ${token}`,
        },
      });
      return res.data?.data;
    },
  });

  const [form] = Form.useForm();

  const onFinish = (values: any) => {
    createCustomerMutation
      .mutateAsync(values)
      .then(() => {
        messageApi.success('Successfully create new customer!');
        onClose();
        return queryClient.invalidateQueries({
          queryKey: ['customers'],
        });
      })
      .catch(() => {
        messageApi.error('Something is wrong');
      });
  };

  return (
    <Modal
      loading={createCustomerMutation.isPending}
      title="Create New Customer"
      destroyOnClose
      footer={null}
      open={open}
      onCancel={onClose}
    >
      {contextHolder}
      <Form
        onFinish={onFinish}
        name="createNewCustomer"
        form={form}
        requiredMark={false}
        style={{
          paddingTop: 20,
        }}
        layout="vertical"
        autoComplete="off"
      >
        <Form.Item
          rules={[
            {
              required: true,
              message: 'Please enter',
            },
          ]}
          name="email"
          layout="vertical"
          label="Customer Email"
        >
          <Input type="email" placeholder="e.g. john.doe@example.com" />
        </Form.Item>
        <Form.Item
          rules={[
            {
              required: true,
              message: 'Please select',
            },
          ]}
          name="beneficiary_type"
          layout="vertical"
          label="Customer Type"
        >
          <Select placeholder="Select value...">
            <Select.Option value="individual">Individual</Select.Option>
            <Select.Option value="corporate">Business</Select.Option>
          </Select>
        </Form.Item>

        <Form.Item shouldUpdate={true} noStyle>
          {() => {
            const customerType = form.getFieldValue('beneficiary_type');

            if (!customerType) {
              return null;
            }

            if (customerType === 'individual') {
              return (
                <>
                  <Form.Item
                    rules={[
                      {
                        required: true,
                        message: 'Please enter',
                      },
                    ]}
                    name="first_name"
                    layout="vertical"
                    label="First Name"
                  >
                    <Input placeholder="e.g. John" />
                  </Form.Item>
                  <Form.Item
                    rules={[
                      {
                        required: true,
                        message: 'Please enter',
                      },
                    ]}
                    name="last_name"
                    layout="vertical"
                    label="Last Name"
                  >
                    <Input placeholder="e.g. Doe" />
                  </Form.Item>
                </>
              );
            }

            return (
              <>
                <Form.Item
                  rules={[
                    {
                      required: true,
                      message: 'Please enter',
                    },
                  ]}
                  name="company_name"
                  layout="vertical"
                  label="Company Name"
                >
                  <Input placeholder="e.g. Acme Ltd" />
                </Form.Item>
              </>
            );
          }}
        </Form.Item>

        <Flex style={{ paddingTop: '18px' }} gap={'middle'} justify="flex-end">
          <Button onClick={onClose}>Cancel</Button>
          <Button type="primary" htmlType="submit">
            Create Customer
          </Button>
        </Flex>
      </Form>
    </Modal>
  );
};
